/**
 * Leaflet L.GridLayer с canvas-рендером IDW-интерполированной тепловой карты.
 *
 * На каждый тайл 256×256:
 * 1. Считаем IDW на грубой сетке 32×32 → 1024 KD-tree запросов вместо 65k.
 * 2. Bilinear-интерполируем grid до 256×256 пикселей.
 * 3. Красим каждый пиксель через palette (та же, что у choropleth).
 *
 * Сама тепловая карта = плавный градиент тех же цветов, что в choropleth.
 * Tooltip на mousemove показывает значение БЛИЖАЙШЕЙ comune (не IDW),
 * чтобы при переключении на choropleth-вид цифры совпадали.
 */

import L from 'leaflet';
import {
  buildIdwIndex,
  bilinearSample,
  hexToRgb,
  idwAt,
  type IdwIndex,
  type IdwPoint,
} from './idw';

interface IDWLayerOptions extends L.GridLayerOptions {
  /** Степень обратного расстояния. Чем выше — тем «локальнее» цвет. */
  power?: number;
  /** Макс число соседей в усреднении. */
  K?: number;
  /** Радиус поиска в lat-degrees (0.3 ≈ 33km). */
  maxRadiusDeg?: number;
  /** Разрешение IDW-сетки внутри тайла. 32 — баланс качества и скорости. */
  gridResolution?: number;
}

// Leaflet extend pattern + typed accessor.
type IDWLayerInstance = L.GridLayer & {
  setData(points: IdwPoint[], colorFn: (v: number) => string): void;
  getIndex(): IdwIndex | null;
};

const IDWLayerClass = L.GridLayer.extend({
  options: {
    tileSize: 256,
    // power=3: ближайшая comune доминирует сильнее → более чёткие переходы
    // между регионами разного значения. power=2 давал слишком «плавную»
    // интерполяцию, цвет «расплывался» далеко от исходной точки.
    power: 3,
    K: 5,
    // 0.10° ≈ 11km — плотно облегает фактическое распределение comuni.
    // В густых регионах (равнины) граница тепловой карты совпадает с
    // береговой линией; в Альпах могут появиться небольшие «дыры» в
    // самых разреженных долинах — это нормально, в них и нет данных.
    maxRadiusDeg: 0.1,
    // 64 ячеек на тайл = ~4px/ячейка для 256×256. На 32 ячейках были
    // видны квадратные зубчатые границы по краям тепловой карты.
    gridResolution: 64,
    // updateWhenZooming: false снижает мерцание при анимации зума.
    updateWhenZooming: false,
    className: 'heat-tile',
  } as IDWLayerOptions,

  initialize(this: IDWLayerInstance & { _index: IdwIndex | null; _colorFn: ((v: number) => string) | null }, options?: IDWLayerOptions) {
    L.Util.setOptions(this, options);
    // L.GridLayer не определяет initialize в типах, но он есть в runtime.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (L.GridLayer.prototype as any).initialize?.call(this, options);
    this._index = null;
    this._colorFn = null;
  },

  setData(this: IDWLayerInstance & { _index: IdwIndex | null; _colorFn: ((v: number) => string) | null }, points: IdwPoint[], colorFn: (v: number) => string) {
    this._index = buildIdwIndex(points);
    this._colorFn = colorFn;
    this.redraw();
  },

  getIndex(this: { _index: IdwIndex | null }): IdwIndex | null {
    return this._index;
  },

  createTile(this: L.GridLayer & {
    _index: IdwIndex | null;
    _colorFn: ((v: number) => string) | null;
    options: IDWLayerOptions;
    _tileCoordsToBounds(coords: L.Coords): L.LatLngBounds;
  }, coords: L.Coords): HTMLCanvasElement {
    const tileSize = (this.options.tileSize as number) || 256;
    const tile = document.createElement('canvas');
    tile.width = tileSize;
    tile.height = tileSize;
    const ctx = tile.getContext('2d');
    if (!ctx || !this._index || !this._colorFn) return tile;

    const bounds = this._tileCoordsToBounds(coords);
    const north = bounds.getNorth();
    const south = bounds.getSouth();
    const west = bounds.getWest();
    const east = bounds.getEast();
    const grid = this.options.gridResolution as number;
    const K = this.options.K as number;
    const power = this.options.power as number;
    const maxRadiusDeg = this.options.maxRadiusDeg as number;

    // 1) IDW-сетка grid×grid внутри тайла.
    const gridValues: Array<number | null> = new Array(grid * grid);
    for (let gy = 0; gy < grid; gy++) {
      const lat = north - ((north - south) * gy) / (grid - 1);
      for (let gx = 0; gx < grid; gx++) {
        const lon = west + ((east - west) * gx) / (grid - 1);
        gridValues[gy * grid + gx] = idwAt(lat, lon, this._index, {
          K,
          power,
          maxRadiusDeg,
        });
      }
    }

    // 2) Bilinear → пиксели + покраска.
    // Используем DVA bilinear: один по значению, второй по маске «есть/нет
    // данных». Альфа = bilinear из {0,1} на 4 углах. Это даёт мягкий fade
    // в прозрачность там, где IDW не нашёл данных (море/радиус), без
    // резкой ступенчатой границы.
    const BASE_ALPHA = 110; // ~43% — подложка с дорогами/реками хорошо читается
    const img = ctx.createImageData(tileSize, tileSize);
    const data = img.data;
    const colorCache = new Map<string, [number, number, number]>();
    for (let py = 0; py < tileSize; py++) {
      const fy = (py / (tileSize - 1)) * (grid - 1);
      const iy = Math.floor(fy);
      const iy2 = Math.min(iy + 1, grid - 1);
      const ty = fy - iy;
      for (let px = 0; px < tileSize; px++) {
        const fx = (px / (tileSize - 1)) * (grid - 1);
        const ix = Math.floor(fx);
        const ix2 = Math.min(ix + 1, grid - 1);
        const tx = fx - ix;

        const v00 = gridValues[iy * grid + ix];
        const v01 = gridValues[iy * grid + ix2];
        const v10 = gridValues[iy2 * grid + ix];
        const v11 = gridValues[iy2 * grid + ix2];

        // Альфа-маска: 1 для cell с данными, 0 для null.
        const a00 = v00 === null ? 0 : 1;
        const a01 = v01 === null ? 0 : 1;
        const a10 = v10 === null ? 0 : 1;
        const a11 = v11 === null ? 0 : 1;
        const aTop = a00 * (1 - tx) + a01 * tx;
        const aBot = a10 * (1 - tx) + a11 * tx;
        const alphaMask = aTop * (1 - ty) + aBot * ty;

        const idx = (py * tileSize + px) * 4;
        if (alphaMask < 0.01) {
          data[idx + 3] = 0;
          continue;
        }

        // Значение: bilinear, null заменяется доступным соседом (чтобы
        // не было артефактов на краях, где часть углов null).
        const v = bilinearSample(gridValues, grid, fx, fy);
        if (v === null || !isFinite(v)) {
          data[idx + 3] = 0;
          continue;
        }

        const hex = this._colorFn(v);
        let rgb = colorCache.get(hex);
        if (!rgb) {
          rgb = hexToRgb(hex);
          colorCache.set(hex, rgb);
        }
        data[idx] = rgb[0];
        data[idx + 1] = rgb[1];
        data[idx + 2] = rgb[2];
        data[idx + 3] = Math.round(BASE_ALPHA * alphaMask);
      }
    }
    ctx.putImageData(img, 0, 0);
    return tile;
  },
});

export function createIDWLayer(options?: IDWLayerOptions): IDWLayerInstance {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new (IDWLayerClass as any)(options) as IDWLayerInstance;
}

export type { IDWLayerInstance };
